// Listens for `paywall:hit` window events dispatched by fetch
// interceptors (see services/api.js — added in same patch). Shows a
// modal explaining which trial limit was hit and pointing the user to
// info@kaizenkanri.nl for an upgrade conversation.
import { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Lock, ExternalLink } from 'lucide-react';

const ACTION_TITLES = {
  'project.create':   'Project limit reached',
  'checklist.create': 'Checklist limit reached',
  'team.create':      'Teams are read-only',
  'team.update':      'Teams are read-only',
  'team.delete':      'Teams are read-only',
  'archive.create':   'Archiving is locked',
  'export.create':    'Export limit reached',
};

export const PaywallModal = () => {
  const [open, setOpen] = useState(false);
  const [payload, setPayload] = useState(null);

  useEffect(() => {
    const handler = (e) => { setPayload(e.detail || {}); setOpen(true); };
    window.addEventListener('paywall:hit', handler);
    return () => window.removeEventListener('paywall:hit', handler);
  }, []);

  if (!payload) return null;
  const title = ACTION_TITLES[payload.action] || 'Trial limit reached';

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-md" data-testid="paywall-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lock className="h-4 w-4 text-amber-500" /> {title}
          </DialogTitle>
          <DialogDescription className="pt-2 text-sm leading-relaxed">
            {payload.reason || 'This action is part of the paid plan.'}
            <br />
            Your trial keeps the rest of the platform fully usable — only this specific action is gated.
          </DialogDescription>
        </DialogHeader>
        <div className="rounded border bg-muted/30 p-3 text-sm space-y-1.5">
          <p className="font-semibold">Want this unlocked?</p>
          <p className="text-muted-foreground">
            Pricing is flexible and depends on which modules your team needs. Email us and we'll set up a quick call.
          </p>
        </div>
        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={() => setOpen(false)} data-testid="paywall-dismiss">
            Maybe later
          </Button>
          <Button asChild data-testid="paywall-contact">
            <a href="mailto:info@kaizenkanri.nl?subject=Kanriva™ subscription enquiry">
              <ExternalLink className="h-4 w-4 mr-2" /> Discuss subscription
            </a>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PaywallModal;
